import { useCallback, useEffect, useRef } from "react";

import { useUnsavedChangesContext } from "../context/UnsavedChangesContext";
import { confirmLeaveNavigation } from "../utils/confirmNavigation";

interface UseUnsavedChangesGuardOptions {
  id: string;
  when: boolean;
  message?: string;
}

export const useUnsavedChangesGuard = ({
  id,
  when,
  message,
}: UseUnsavedChangesGuardOptions) => {
  const { registerGuard, unregisterGuard } = useUnsavedChangesContext();
  const savedRef = useRef(false);

  useEffect(() => {
    registerGuard({ id, when: when && !savedRef.current, message });
    return () => unregisterGuard(id);
  }, [id, when, message, registerGuard, unregisterGuard]);

  useEffect(() => {
    if (!when || savedRef.current) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);

  const confirmLeave = useCallback(async () => {
    if (!when || savedRef.current) {
      return true;
    }

    return confirmLeaveNavigation(message);
  }, [when, message]);

  const markSaved = useCallback(() => {
    savedRef.current = true;
    registerGuard({ id, when: false, message });
  }, [id, message, registerGuard]);

  const resetSaved = useCallback(() => {
    savedRef.current = false;
    registerGuard({ id, when, message });
  }, [id, when, message, registerGuard]);

  return { confirmLeave, markSaved, resetSaved };
};
